import { BellIcon } from 'lucide-react';

import { defineModule } from '@/shared/registry/types';

import { useUnreadCount } from './api';
import { NotificationsBell } from './components/NotificationsBell';
import { PushSync } from './push/PushSync';
import { registerPushMessages } from './push/messages';
import { registerNotificationRealtime } from './realtime';

/** Sidebar counter: unread notifications (kept live by the realtime handlers). */
const useUnreadCounter = (): number | undefined => useUnreadCount().data;

/**
 * `notifications` module: the notification center (docs/modules/notifications.md) — the top-bar bell with its dropdown,
 * `/notifications` (filters, grouped by day) and `/notifications/settings` (per-type delivery, e-mail frequency,
 * pop-ups and desktop notifications on this device, web push). Other modules render their notification texts through their
 * own `notifications.<event>` keys or `notificationRenderers`.
 */
export const module = defineModule({
  id: 'notifications',
  routes: [
    {
      path: 'notifications',
      children: [
        {
          index: true,
          lazy: async () => ({ Component: (await import('./pages/NotificationsPage')).default }),
        },
        {
          path: 'settings',
          lazy: async () => ({ Component: (await import('./pages/NotificationSettingsPage')).default }),
        },
      ],
    },
  ],
  nav: [
    {
      id: 'notifications',
      labelKey: 'notifications:nav.title',
      icon: BellIcon,
      to: '/notifications',
      order: 10,
      section: 'primary',
      useCounter: useUnreadCounter,
    },
  ],
  topBarWidgets: [
    { id: 'notifications', order: 10, component: NotificationsBell },
    // Invisible: registers this browser's push subscription again at every start (web push, push/PushSync.tsx).
    { id: 'notifications-push-sync', order: 11, component: PushSync },
  ],
  setup: (context) => {
    registerNotificationRealtime(context);
    registerPushMessages(context);
  },
});
